import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Button } from '@my-swipestox/components';
import styled from 'styled-components';
import { ChevronLeft } from 'react-feather';

import api from '../../api';
import constants from '../../constants';
import Loader from '../../components/loader/Loader';
import useScript from "../../hooks/useScript";

const Header = styled.div`
  padding-left: 24px;
  padding-bottom: 16px;
`;

const BackButton = styled(Button)`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${(props) => props.theme.color.primary};
  margin-right: 36px;
  position: relative;
  bottom: -6px;
`;

const BackButtonText = styled.span`
  vertical-align: middle;
  font-weight: 400;
`;

const ErrorContainer = styled.div `
  padding: 32px;
`;

const TryAgainButton = styled(Button)`
  margin-top: 24px
`;

const Content = styled.div`
  width: 100%;
  >div { width: 100%; }
`;

const Title = styled.span`
  font-size: 20px;
  color: ${(props) => props.theme.color.headerFill};
  margin-left: 16px;
  vertical-align: middle;
  font-weight: 500;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const LoaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 40%;
  min-height: 400px;
`;

const IframeContainer = styled.div`
  iframe {
    width: 100%;
    @media (max-width: 768px) {
      min-height: 60vh;
    }
    min-height: 80vh;
  }
`;

const Praxis = (props) => {
  useScript(constants.PRAXIS.LIBRARY_URL);
  const { t } = useTranslation();
  const containerRef = useRef();
  const { fullAccount, amount, intent, gateway, onBack } = props;

  const praxisAuthTokenQuery = useQuery(
    constants.QUERY_NAMES.getPraxisAuthToken,
    () => api.queries.getPraxisAuthToken({
      terminal_id: fullAccount.terminal_id,
      gateway,
      intent,
      amount,
      currency: 'USD',
    }),
    { enabled: false },
  );

  useEffect(() => {
    praxisAuthTokenQuery.refetch({ enabled: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, intent]);

  useEffect(() => {
    if (praxisAuthTokenQuery.data?.auth_token) {
      const cashier = window.PraxisCashier({
        auth_token: praxisAuthTokenQuery.data?.auth_token,
        container: containerRef.current,
        autoclose: false,
        mode: 'iframe',
        locale: 'en-GB',
      });

      cashier.render();
    }

    return () => {
      if (containerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        containerRef.current.innerHTML = '';
      }
    }
  }, [praxisAuthTokenQuery.data?.auth_token]);

  const onTryAgain = () => {
    praxisAuthTokenQuery.refetch({ enabled: true });
  };

  return (
    <>
      <Header>
        <BackButton onClick={onBack}>
          <ChevronLeft />
          <BackButtonText>{t('FUND_ACCOUNT.BACK')}</BackButtonText>
        </BackButton>
        <Title>
          {t(`FUNDING.${intent.toUpperCase()}_VIA`)} {t(`FUNDING.${gateway}`)}
        </Title>
      </Header>
      <Content>
        {praxisAuthTokenQuery.isFetching && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
        {praxisAuthTokenQuery.data?.error && !praxisAuthTokenQuery.isFetching && (
          <ErrorContainer>
            <p>{t('STATUS_CODE.TOO_MANY_WITHDRAWALS')}</p>
            <TryAgainButton onClick={onTryAgain}>
              {t('FUND_ACCOUNT.DEPOSIT_COMPLETED_TRY_AGAIN')}
            </TryAgainButton>
          </ErrorContainer>
        )}
        <IframeContainer ref={containerRef} />
      </Content>
    </>
  );
};

export default Praxis;
